import React from 'react';
import { useSetAtom } from 'jotai';
import { IconButton } from '@smart-tv/components/icon-button/icon-button';
import ArrowBackIcon from '@smart-tv/components/icons/arrow-back-icon';
import Typography from '@smart-tv/components/typography';
import { IS_BROWSER } from '@smart-tv/utils/is-browser';
import {
  ContentInfoAssetResponse,
  GetContentInfoAssetParams
} from '@smart-tv/utils/kmm/getContentInfoForAsset';
import { descalePlayerAtom } from '../atoms';
import useProductSheetInfo from './use-product-sheet-info';
import PlayerProductSheetSpinner from './player-product-sheet-info-spinner';
import SeasonEpisodeInfoField from './player-product-sheet-info-season-episode';
import CombinedStringsIconWrapper from './player-product-sheet-info-combined-strings-icons';
import DescriptionField from './player-product-sheet-info-description';
import ProgramMetadata from './player-product-sheet-info-metadata';
import styles from './player-product-sheet-info.module.scss';

const PlayerProductSheetInfo = ({ sid, eventId, mediaId }: GetContentInfoAssetParams) => {
  const { contentInfoAsset, error } = useProductSheetInfo({ sid, eventId, mediaId });
  const setDescalePlayer = useSetAtom(descalePlayerAtom);

  return (
    <div
      className={styles['player-product-sheet-info']}
      onMouseDown={(event) => event.preventDefault()}
    >
      {IS_BROWSER && (
        <IconButton
          size="xl"
          aria-label="Luk"
          onClick={() => setDescalePlayer(false)}
          className={styles['player-product-sheet-info__close-button']}
          icon={<ArrowBackIcon />}
        />
      )}
      {contentInfoAsset ? (
        <div className={styles['player-product-sheet-info__content-wrapper']}>
          {(!!contentInfoAsset.seasonNumber || !!contentInfoAsset.episodeNumber) && (
            <SeasonEpisodeInfoField
              seasonNumber={contentInfoAsset.seasonNumber}
              episodeNumber={contentInfoAsset.episodeNumber}
            />
          )}
          <Typography variant="h4" component="h1" fontWeight="bold">
            {contentInfoAsset.title ?? 'Ingen titel'}
          </Typography>
          {renderProgramMeta(contentInfoAsset) && (
            <ProgramMetadata
              channelName={contentInfoAsset.infoBlock?.providerName ?? undefined}
              releaseYear={
                contentInfoAsset.infoBlock?.year
                  ? String(contentInfoAsset.infoBlock.year)
                  : undefined
              }
              durationInSeconds={contentInfoAsset.infoBlock?.duration ?? undefined}
            />
          )}
          <CombinedStringsIconWrapper contentInfoAsset={contentInfoAsset} />
          {contentInfoAsset.episodeTitle && (
            <Typography
              variant="body"
              component="h2"
              fontWeight="bold"
              className={styles['player-product-sheet-info__description-headline']}
            >
              {contentInfoAsset.episodeTitle}
            </Typography>
          )}
          <DescriptionField
            description={contentInfoAsset.description}
            hasHeadline={!!contentInfoAsset.episodeTitle}
          />
        </div>
      ) : (
        <PlayerProductSheetSpinner error={error} />
      )}
    </div>
  );
};

const renderProgramMeta = (contentInfoAsset: ContentInfoAssetResponse) => {
  return (
    !!contentInfoAsset.infoBlock?.providerName ||
    !!contentInfoAsset.infoBlock?.year ||
    !!contentInfoAsset.infoBlock?.duration
  );
};

export default PlayerProductSheetInfo;
